import { useCallback, useEffect, useRef } from 'react';
import { useChatContext } from '../../../contexts/ChatContext';
import { useEmailContext } from '../../../contexts/EmailContext';
import { useAppContext } from '../../../AppContext';
import { useTelephonyContext } from '../../../contexts/TelephonyContext';
import postEvent from '../../../services/postEvent';
import getCurrentTime from '../../../utils/timeFactory';
import { datadogLogs } from '@datadog/browser-logs';
import { FOCUS_PERIOD_KEY } from '../../../constants/localStorage';

interface FocusEntry {
  contactId: string;
  channel: 'CHAT' | 'EMAIL';
  initiationMethod?: string;
  timestamp: string;
}

export function useFocusPeriod(): void {
  const { selectedChat, chatContacts } = useChatContext();
  const { selectedEmail, emailContacts, emailData } = useEmailContext();
  const { agent } = useAppContext();
  const { voiceContact } = useTelephonyContext();

  const agentRef = useRef(agent);
  agentRef.current = agent;

  const emailDataRef = useRef(emailData);
  emailDataRef.current = emailData;

  const endCurrentFocus = useCallback((endedAt: Date): void => {
    const stored = localStorage.getItem(FOCUS_PERIOD_KEY);
    if (!stored) return;

    const entry = JSON.parse(stored) as FocusEntry;
    localStorage.removeItem(FOCUS_PERIOD_KEY);

    void postEvent(
      entry.contactId,
      'focus',
      { startedAt: new Date(entry.timestamp), endedAt },
      entry.channel,
      entry.initiationMethod
    ).catch((error: Error) => {
      datadogLogs.logger.error('Failed to post focus event', { error, agent: agentRef.current?.getName() });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedChat) return;

    const currentTime = getCurrentTime();
    endCurrentFocus(currentTime);

    const entry: FocusEntry = { contactId: selectedChat, channel: 'CHAT', timestamp: currentTime.toISOString() };
    localStorage.setItem(FOCUS_PERIOD_KEY, JSON.stringify(entry));
  }, [selectedChat, endCurrentFocus]);

  useEffect(() => {
    if (!selectedEmail || voiceContact?.getState().type === connect.ContactStateType.CONNECTED) return;

    const currentTime = getCurrentTime();
    endCurrentFocus(currentTime);

    const entry: FocusEntry = {
      contactId: selectedEmail,
      channel: 'EMAIL',
      initiationMethod: emailDataRef.current[selectedEmail]?.initiationMethod?.toUpperCase(),
      timestamp: currentTime.toISOString()
    };
    localStorage.setItem(FOCUS_PERIOD_KEY, JSON.stringify(entry));
  }, [selectedEmail, voiceContact, endCurrentFocus]);

  useEffect(() => {
    const stored = localStorage.getItem(FOCUS_PERIOD_KEY);
    if (!stored) return;

    const entry = JSON.parse(stored) as FocusEntry;
    const contactList = entry.channel === 'CHAT' ? chatContacts : emailContacts;

    if (!contactList.includes(entry.contactId)) {
      endCurrentFocus(getCurrentTime());
    }
  }, [chatContacts, emailContacts, endCurrentFocus]);

  useEffect(() => {
    if (voiceContact?.getState().type !== connect.ContactStateType.CONNECTED) return;

    const stored = localStorage.getItem(FOCUS_PERIOD_KEY);
    if (stored && (JSON.parse(stored) as FocusEntry).channel === 'EMAIL') {
      localStorage.removeItem(FOCUS_PERIOD_KEY);
    }
  }, [voiceContact]);
}
